import ActionSuccessToast from '../ActionSuccessToast'
import SetupDetailsEditor from './SetupDetailsEditor'
import SetupStopInput from './SetupStopInput'
import { projectSetupLifecycle, hasSubmittedSetupOrders } from '../../lib/positionListStage.js'
// EF-193: replace the hover-only X + trashcan controls on the Position
// Workspace working-setup card with always-visible Edit + Delete buttons,
// shared across desktop and mobile. Strict fail-closed semantics on every
// broker-resolution and cancel failure path — half-replaced setups and
// ghost broker handles are worse than no replacement and no clear.

import { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import { Pencil, Trash2, Check } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../hooks/useAuth'
import { useBiometricGate } from '../../hooks/useBiometricGate'
import { useAccountNicknames } from '../../hooks/useAccountNicknames'
import { useOrderGateway } from '../../hooks/useOrderGateway'
import OrderConfirmationModal from '../OrderConfirmationModal'
import PrivateValue from '../PrivateValue'
import {
  resolveCancelableTrackedOrders,
  executeCancelPayloads,
  confirmCanceledTrackedOrders,
} from '../../lib/cancelOrdersByTrackedIds'
import { buildSetupOrderRequests } from '../../lib/setupOrderRequests'
import { splitGroupHasPartialFailure } from '../../lib/bracketOrderGroups.js'
import { buildSetupRepairPlan } from '../../lib/setupRepairPlan.js'
import { isRetiredSetup, retireUnfilledSetup } from '../../lib/workspace/setupRetirement.js'

const DEFAULT_RISK_PCT = 0.075
const REPAIR_REFRESH_TIMEOUT_MS = 5000

const fmtCurrency = (val) => {
  if (val == null) return '—'
  return Number(val).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const ROLE_MAP = { TARGET: 'TARGET_ENTRY', RUNNER: 'RUNNER_ENTRY', ENTRY: 'ENTRY' }

export default function WorkingSetupCard({
  project: storedProject,
  brokerEntryStatus = null,
  symbol,
  readOnly: requestedReadOnly = false,
  onSelect,
  stageForSending: requestedStageForSending = false,
  onPositionRefresh,
  onMetadataChanged,
  cancellationPending = false,
  onCancellationPendingChange,
  repairRequested = false,
}) {
  const project = projectSetupLifecycle(storedProject)
  const stageForSending = requestedStageForSending && !hasSubmittedSetupOrders(storedProject)
  const readOnly = requestedReadOnly || isRetiredSetup(project)
  const { user } = useAuth()
  const { getNickname } = useAccountNicknames()
  const biometricGate = useBiometricGate()

  // ── Card-level state ────────────────────────────────────────────────────
  const [editOpen, setEditOpen] = useState(false)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [notice, setNotice] = useState(null)
  const [actionPending, setActionPending] = useState(false)
  const [editError, setEditError] = useState(null)
  const [terminalIdsBanner, setTerminalIdsBanner] = useState(false)
  const [repairOpen, setRepairOpen] = useState(false)
  const [repairPlan, setRepairPlan] = useState(null)
  const repairRequestHandled = useRef(false)
  const hasEntryFill = ['filled', 'partially filled'].includes(brokerEntryStatus || project?.setup_status)

  useEffect(() => {
    if (hasEntryFill) {
      setEditOpen(false)
      setRepairOpen(false)
    }
  }, [hasEntryFill])

  // ── Editing field state ─────────────────────────────────────────────────
  const [entryType, setEntryType] = useState(project?.entry_type || 'Pullback')
  const [triggerPrice, setTriggerPrice] = useState(project?.trigger_price || '')
  const [stopPrice, setStopPrice] = useState(project?.pattern_failure_price || '')
  const [targetPrice, setTargetPrice] = useState(project?.sell_target_price || '')
  const [sizePct, setSizePct] = useState(project?.recommended_size_pct ?? 9)
  const [accounts, setAccounts] = useState([])
  const [selectedAccounts, setSelectedAccounts] = useState(new Set())

  // Notice auto-dismiss for success only — error tones must persist until the
  // user dismisses so a partial-cancel failure cannot disappear silently.
  useEffect(() => {
    if (!notice || notice.tone !== 'success') return
    const t = setTimeout(() => setNotice(null), 12000)
    return () => clearTimeout(t)
  }, [notice])

  // ── Account load — pre-select rows in project.account_sizing or all ─────
  useEffect(() => {
    if (!user) return
    let cancelled = false
    ;(async () => {
      const { data } = await supabase
        .from('schwab_accounts')
        .select('account_number, nick_name, account_type, equity')
        .eq('user_id', user.id)
      if (cancelled) return
      const accts = (data || []).map(a => {
        const eq = a.equity
        const equityVal = typeof eq === 'object' ? (eq.liquidationValue || eq.totalAccountValue || 0) : Number(eq) || 0
        return { account_number: a.account_number, nick_name: a.nick_name || a.account_type, equity: equityVal }
      }).filter(a => a.equity > 0)
      setAccounts(accts)

      const sizingNumbers = (project?.account_sizing || []).map(s => s.account_number)
      if (sizingNumbers.length > 0) {
        setSelectedAccounts(new Set(accts.filter(a => sizingNumbers.includes(a.account_number)).map(a => a.account_number)))
      } else {
        setSelectedAccounts(new Set(accts.map(a => a.account_number)))
      }
    })()
    return () => { cancelled = true }
  }, [user, project?.id])

  // Refresh order fields only when order data changes; a journal save preserves order drafts.
  useEffect(() => {
    setEntryType(project?.entry_type || 'Pullback')
    setTriggerPrice(project?.trigger_price || '')
    setStopPrice(project?.pattern_failure_price || '')
    setTargetPrice(project?.sell_target_price || '')
    setSizePct(project?.recommended_size_pct ?? 9)
  }, [project?.id, project?.entry_type, project?.trigger_price, project?.pattern_failure_price, project?.sell_target_price, project?.recommended_size_pct])

  // EF-193 post-Codex amend: rehydrate the editable draft (INCLUDING
  // selectedAccounts) from persisted project state. toggleAccount mutates
  // selectedAccounts in place, and a previous "open → toggle → cancel"
  // round-trip would otherwise leave stale checkbox state visible the
  // next time Edit opens. The next save would then build gatewayOrders
  // against that stale subset, routing replacement orders to accounts the
  // user thought they had deselected. Reset is wired to BOTH Edit open
  // and the Cancel button.
  const resetEditDraftFromProject = useCallback(() => {
    setEntryType(project?.entry_type || 'Pullback')
    setTriggerPrice(project?.trigger_price || '')
    setStopPrice(project?.pattern_failure_price || '')
    setTargetPrice(project?.sell_target_price || '')
    setSizePct(project?.recommended_size_pct ?? 9)
    setEditError(null)
    setTerminalIdsBanner(false)
    const sizingNumbers = (project?.account_sizing || []).map(s => s.account_number)
    if (sizingNumbers.length > 0) {
      setSelectedAccounts(new Set(
        accounts.filter(a => sizingNumbers.includes(a.account_number)).map(a => a.account_number)
      ))
    } else {
      setSelectedAccounts(new Set(accounts.map(a => a.account_number)))
    }
  }, [
    project?.setup_pattern, project?.entry_type, project?.trigger_price,
    project?.pattern_failure_price, project?.sell_target_price,
    project?.recommended_size_pct, project?.account_sizing, accounts,
  ])

  // ── Sizing math (mirrors SetupStagingPanel) ─────────────────────────────
  const accountSizing = useMemo(() => {
    const trigger = Number(triggerPrice) || 0
    const stop = Number(stopPrice) || 0
    const alloc = Number(sizePct) / 100
    if (!trigger || !stop) return accounts.map(a => ({ ...a, shares: 0, totalCost: 0, dollarRisk: 0 }))
    const stopDist = Math.abs(trigger - stop)
    return accounts.map(a => {
      const perTranche = a.equity * alloc
      const allocShares = Math.floor(perTranche / trigger)
      const maxLoss = perTranche * DEFAULT_RISK_PCT
      const riskShares = stopDist > 0 ? Math.floor(maxLoss / stopDist) : allocShares
      const shares = Math.min(allocShares, riskShares)
      return { ...a, shares, totalCost: shares * trigger, dollarRisk: shares * stopDist }
    })
  }, [accounts, triggerPrice, stopPrice, sizePct])

  // ── handleEditResults — fail-closed branch ordering A → B → C → D ───────
  const handleEditResults = useCallback(async (results) => {
    const arr = Array.isArray(results) ? results : []

    // EF-193: successfulLiveTracked is filtered to PLACE/STAGE results only.
    // Canceled orderIds must NEVER be persisted as new live tracking —
    // they're dead handles at Schwab. Skip rows are filtered explicitly even
    // though the success === true check would also exclude them; the
    // explicit filter codifies the invariant (v5 P3 fold).
    const successfulLiveTracked = arr
      .filter(r => r && r.success === true && r.orderId != null)
      .filter(r => !r.skippedDueToCancelFailure)
      .filter(r => r.gatewayBatchAction !== 'CANCEL')
      .filter(r => r.rollbackAttempted !== true)
      .map(r => ({
        accountNumber: r.account_number ?? r.accountNumber ?? null,
        orderId: r.orderId,
        bracketGroupId: r.bracketGroupId ?? null,
        role: ROLE_MAP[r.bracketRole] || 'ENTRY',
        placedAt: new Date().toISOString(),
      }))

    const everySuccess = arr.length > 0 && arr.every(r => r.success)
    const anyCancelSkipped = arr.some(r => r.skippedDueToCancelFailure)
    const hasPartialSplitFailure = splitGroupHasPartialFailure(arr)
    const everyFailed = arr.length > 0 && arr.every(r => !r.success)
    // EF-193 post-Codex amend: treat non-CANCEL rows as the "new live setup"
    // outcome. If none of them succeeded, replacement did not go live and
    // the setup must NOT remain marked working — otherwise the row gets
    // stuck at `working + schwab_order_ids: []` and the Edit button is
    // disabled by the editDisabled guard (working + empty tracked → legacy
    // row tooltip), leaving the user with no in-card retry path.
    const liveAttemptRows = arr.filter(r => r?.gatewayBatchAction !== 'CANCEL')
    const hasLiveAttemptRows = liveAttemptRows.length > 0
    const everyLiveAttemptFailed =
      hasLiveAttemptRows && liveAttemptRows.every(r => r?.success !== true)

    const sizing = accountSizing
      .filter(a => selectedAccounts.has(a.account_number))
      .map(a => ({
        account_number: a.account_number,
        shares: a.shares,
        max_loss: a.dollarRisk,
      }))

    const baseMetadata = {
      entry_type: entryType,
      trigger_price: Number(triggerPrice) || null,
      pattern_failure_price: Number(stopPrice) || null,
      sell_target_price: Number(targetPrice) || null,
      recommended_size_pct: Number(sizePct) || null,
      account_sizing: sizing,
      updated_at: new Date().toISOString(),
    }

    // Branch order: A → B → C → D (anyCancelSkipped wins over success/fail mix).
    let updateFields = null
    let nextNotice = null

    if (everySuccess) {
      // A. clean success
      updateFields = {
        ...baseMetadata,
        schwab_order_ids: successfulLiveTracked,
        setup_status: 'working',
      }
      nextNotice = { tone: 'success', text: `${symbol} setup edited — replacement orders are live at Schwab.` }
    } else if (anyCancelSkipped) {
      // B. cancel-failure-skipped PLACE — original IDs still live; preserve.
      updateFields = null
      nextNotice = {
        tone: 'error',
        text: `Could not cancel existing ${symbol} setup orders — replacement orders were not placed. Original orders are still live at Schwab. Review Working Orders before retrying.`,
      }
    } else if (hasPartialSplitFailure || everyFailed || everyLiveAttemptFailed) {
      // C. setup_status: 'failed' + ORPHAN_NEEDS_CLEANUP for the rollback-failed rows.
      // EF-193 post-Codex amend: everyLiveAttemptFailed catches the
      // CANCEL-success + PLACE-all-fail case that would otherwise land in
      // branch D as `working + []` and disable the Edit button. Always
      // persist schwab_order_ids (including empty) so stale IDs from the
      // pre-edit setup don't linger on the failed row.
      const cleanupOrphans = arr
        .filter(r => r && r.manualCleanupRequired === true && r.orderId != null)
        .map(r => ({
          accountNumber: r.account_number ?? r.accountNumber ?? null,
          orderId: r.orderId,
          bracketGroupId: r.bracketGroupId ?? null,
          role: 'ORPHAN_NEEDS_CLEANUP',
          placedAt: new Date().toISOString(),
        }))
      const tracked = [...successfulLiveTracked, ...cleanupOrphans]
      updateFields = {
        ...baseMetadata,
        setup_status: 'failed',
        schwab_order_ids: tracked,
      }
      nextNotice = { tone: 'error', text: `${symbol} setup edit failed — review the modal for per-account detail.` }
    } else {
      // D. mixed — some PLACE successes, some plain failures, no split-group partial, no cancel skip
      updateFields = {
        ...baseMetadata,
        schwab_order_ids: successfulLiveTracked,
        setup_status: 'working',
      }
      nextNotice = { tone: 'error', text: `${symbol} setup edited with partial failures — see modal for detail.` }
    }

    if (updateFields && project?.id) {
      await supabase.from('position_projects').update(updateFields).eq('id', project.id)
    }
    setNotice(nextNotice)
    setEditOpen(false)
    if (onPositionRefresh) await onPositionRefresh()
  }, [accountSizing, selectedAccounts, entryType, triggerPrice, stopPrice, targetPrice, sizePct, project?.id, symbol, onPositionRefresh])

  const handleRepairResults = useCallback(async (results) => {
    const arr = Array.isArray(results) ? results : []
    if (arr.some(row => row?.skippedDueToPreconditionFailure || row?.skippedDueToCancelFailure)) {
      setNotice({
        tone: 'error',
        text: `Could not safely reset the incomplete ${symbol} account. No replacement orders were placed; review the order results before retrying.`,
      })
      return
    }

    const successfulLiveTracked = arr
      .filter(row => row?.success === true && row?.orderId != null)
      .filter(row => row?.gatewayBatchAction !== 'CANCEL')
      .filter(row => row?.rollbackAttempted !== true)
      .map(row => ({
        accountNumber: row.account_number ?? row.accountNumber ?? null,
        orderId: row.orderId,
        bracketGroupId: row.bracketGroupId ?? null,
        role: ROLE_MAP[row.bracketRole] || 'ENTRY',
        placedAt: new Date().toISOString(),
      }))
    const cleanupOrphans = arr
      .filter(row => row?.manualCleanupRequired === true && row?.orderId != null)
      .map(row => ({
        accountNumber: row.account_number ?? row.accountNumber ?? null,
        orderId: row.orderId,
        bracketGroupId: row.bracketGroupId ?? null,
        role: 'ORPHAN_NEEDS_CLEANUP',
        placedAt: new Date().toISOString(),
      }))
    const tracked = [
      ...(repairPlan?.retainedTrackedOrders || []),
      ...successfulLiveTracked,
      ...cleanupOrphans,
    ]
    const allPlaceSucceeded = arr.some(row => row?.gatewayBatchAction !== 'CANCEL')
      && arr.filter(row => row?.gatewayBatchAction !== 'CANCEL').every(row => row?.success === true)

    if (project?.id) {
      await supabase.from('position_projects').update({
        schwab_order_ids: tracked,
        setup_status: tracked.length > 0 ? 'working' : 'failed',
        stage: tracked.length > 0 ? 'working' : project?.stage,
        updated_at: new Date().toISOString(),
      }).eq('id', project.id)
    }
    setRepairOpen(false)
    setRepairPlan(null)
    setNotice(allPlaceSucceeded
      ? { tone: 'success', text: `${symbol} repair complete — every selected account now has its setup entry orders.` }
      : { tone: 'error', text: `${symbol} still needs attention — some repair orders did not reach Schwab. Review the results and retry only the remaining account.` })
    if (onPositionRefresh) await onPositionRefresh()
  }, [onPositionRefresh, project?.id, project?.stage, repairPlan, symbol])

  // Repair and Edit intentionally have separate gateway state. A repair must
  // preserve complete accounts, while Edit replaces the entire setup.
  const repairGateway = useOrderGateway({
    biometricGate,
    onOrdersChanged: handleRepairResults,
  })

  // ── Own gateway instance for Edit ────────────────────────────────────────
  const gateway = useOrderGateway({
    biometricGate,
    onOrdersChanged: handleEditResults,
  })

  const prepareRepair = useCallback(async () => {
    setActionPending(true)
    setRepairPlan(null)
    try {
      let session = null
      try {
        const { data } = await supabase.auth.getSession()
        session = data?.session || null
      } catch { /* resolver fails closed without a session */ }

      const tracked = Array.isArray(project?.schwab_order_ids) ? project.schwab_order_ids : []
      const { cancelPayloads, fetchError } = await resolveCancelableTrackedOrders(
        tracked,
        session || {},
        { timeoutMs: REPAIR_REFRESH_TIMEOUT_MS },
      )
      if (fetchError) {
        setNotice({
          tone: 'error',
          text: 'Could not refresh Schwab order state, so no orders were changed. Try Repair again after the connection is current.',
        })
        return
      }

      const savedSizing = (Array.isArray(project?.account_sizing) ? project.account_sizing : [])
        .map(row => ({
          account_number: row.account_number,
          shares: Number(row.shares) || 0,
          dollarRisk: Number(row.max_loss) || 0,
        }))
      const plan = buildSetupRepairPlan({
        accountSizing: savedSizing,
        trackedOrders: tracked,
        cancelPayloads,
      })
      if (plan.complete) {
        setNotice({ tone: 'success', text: `${symbol} is already complete at Schwab. No repair orders were sent.` })
        if (onPositionRefresh) await onPositionRefresh()
        return
      }
      const selectedAccountNumbers = new Set(plan.affectedAccountNumbers)
      const built = buildSetupOrderRequests({
        symbol,
        entryType: project?.entry_type || 'Pullback',
        triggerPrice: project?.trigger_price,
        stopPrice: project?.pattern_failure_price,
        targetPrice: project?.sell_target_price,
        accountSizing: savedSizing,
        selectedAccountNumbers,
        getNickname,
      })
      if (built.gatewayOrders.length === 0) {
        setNotice({ tone: 'error', text: 'Repair could not rebuild the saved account sizing. No orders were changed.' })
        return
      }
      setRepairPlan({ ...plan, ...built })
      setRepairOpen(true)
    } finally {
      setActionPending(false)
    }
  }, [getNickname, onPositionRefresh, project, symbol])

  const confirmRepair = useCallback(() => {
    if (!repairPlan) return
    if (repairPlan.cancelPayloads.length > 0) {
      repairGateway.showConfirmation('REPAIR_SETUP', symbol, repairPlan.orderSummaries, null, [
        { action: 'CANCEL', orders: repairPlan.cancelPayloads },
        { action: 'PLACE', orders: repairPlan.gatewayOrders },
      ])
    } else {
      repairGateway.showConfirmation('STAGE_ORDERS', symbol, repairPlan.orderSummaries, repairPlan.gatewayOrders, null)
    }
    setRepairOpen(false)
  }, [repairGateway, repairPlan, symbol])

  useEffect(() => {
    if (!repairRequested || repairRequestHandled.current || readOnly || hasEntryFill) return
    repairRequestHandled.current = true
    prepareRepair()
  }, [prepareRepair, readOnly, repairRequested, hasEntryFill])

  // ── Edit Save click handler (fail-closed) ───────────────────────────────
  const handleEditSave = useCallback(async () => {
    setEditError(null)
    setTerminalIdsBanner(false)
    const trigger = Number(triggerPrice)
    const stop = Number(stopPrice)
    if (!(trigger > 0) || !(stop > 0) || !Number.isFinite(stop)) {
      setEditError('Trigger and stop must be positive numbers.')
      return
    }

    setActionPending(true)
    try {
      let session = null
      try {
        const { data } = await supabase.auth.getSession()
        session = data?.session || null
      } catch { /* helper handles missing session */ }

      const tracked = Array.isArray(project?.schwab_order_ids) ? project.schwab_order_ids : []
      const { cancelPayloads, fetchError } = await resolveCancelableTrackedOrders(tracked, session || {})

      if (fetchError) {
        setNotice({
          tone: 'error',
          text: 'Could not verify tracked Schwab orders; no broker changes were made. Try again, or cancel manually in ThinkorSwim.',
        })
        return
      }

      const { orderSummaries, gatewayOrders } = buildSetupOrderRequests({
        symbol,
        entryType,
        triggerPrice: trigger,
        stopPrice: stop,
        targetPrice: Number(targetPrice),
        accountSizing,
        selectedAccountNumbers: selectedAccounts,
        getNickname,
      })

      if (gatewayOrders.length === 0) {
        setEditError('No accounts have positive shares at the current sizing.')
        return
      }

      if (cancelPayloads.length === 0) {
        // All tracked orders are already terminal at Schwab — degrade to a
        // simple STAGE_ORDERS placement (no cancel batch).
        setTerminalIdsBanner(true)
        gateway.showConfirmation('STAGE_ORDERS', symbol, orderSummaries, gatewayOrders, null)
      } else {
        gateway.showConfirmation('REPLACE_SETUP', symbol, orderSummaries, null, [
          { action: 'CANCEL', orders: cancelPayloads },
          { action: 'PLACE', orders: gatewayOrders },
        ])
      }
      setEditOpen(false)
    } finally {
      setActionPending(false)
    }
  }, [triggerPrice, stopPrice, targetPrice, symbol, entryType, accountSizing, selectedAccounts, project?.schwab_order_ids, getNickname, gateway])

  // ── Delete fire click handler (fail-closed) ─────────────────────────────
  const handleDeleteFire = useCallback(async () => {
    if (!user || !project?.id || readOnly) return
    let setupRetired = false
    setActionPending(true)
    onCancellationPendingChange?.(project.id, true)
    try {
      if (!['idea', 'planned', 'working'].includes(project.stage) || project.trade_id || project.setup_status === 'filled') {
        throw new Error('This setup belongs to an active or completed position and cannot be canceled here.')
      }
      if (!project.updated_at) throw new Error('Refresh this setup before canceling so its current version can be verified.')
      // Match the editor's application-only retirement path. Only an
      // unfilled idea/planned record without order references can take it;
      // broker-linked records must still pass verification below.
      if (project.schwab_order_ids != null && !Array.isArray(project.schwab_order_ids)) {
        throw new Error('Setup order references are unavailable. Refresh to reconcile before canceling.')
      }
      const tracked = project.schwab_order_ids || []
      if (!tracked.length && (project.stage === 'working' || project.setup_status === 'working')) {
        throw new Error('Cancellation incomplete — Needs attention. This working setup has no tracked order IDs. Reconcile its broker orders before retiring it.')
      }

      if (!tracked.length) {
        await retireUnfilledSetup(supabase, project, { userId: user.id })
        setupRetired = true
        setNotice(null)
        setDeleteConfirmOpen(false)
        if (onPositionRefresh) await onPositionRefresh()
        if (onMetadataChanged) onMetadataChanged()
        return
      }

      let bioOk = false
      let brokerWriteProof = null
      try {
        const { verified, error: bioError, brokerWriteProof: proof } = await biometricGate.verify()
        bioOk = verified
        brokerWriteProof = proof || null
        if (!verified) {
          setNotice({ tone: 'error', text: bioError || 'Biometric verification failed' })
          return
        }
      } catch (err) {
        setNotice({ tone: 'error', text: err?.message || 'Biometric verification failed' })
        return
      }
      if (!bioOk) return

      // Working + tracked path. Resolve + execute via the split helpers so
      // the initial lookup precedes exact terminal confirmation.
      let session = null
      try {
        const { data } = await supabase.auth.getSession()
        session = data?.session || null
      } catch { /* helper handles missing session */ }

      const { cancelPayloads, fetchError } = await resolveCancelableTrackedOrders(tracked, session || {}, { timeoutMs: REPAIR_REFRESH_TIMEOUT_MS, entryOnly: true, expectedSymbol: symbol })
      if (fetchError) {
        setNotice({
          tone: 'error',
          text: `Could not verify tracked Schwab orders; setup was NOT retired. ${fetchError.message || 'Refresh to reconcile before retrying.'}`,
        })
        return
      }

      const confirmAndFinalize = async () => {
        const confirmation = await confirmCanceledTrackedOrders(tracked, session || {}, { requireUnfilled: true })
        if (!confirmation.confirmed) {
          const remaining = confirmation.remainingTrackedOrders?.length > 0
            ? confirmation.remainingTrackedOrders
            : tracked
          // Keep the full original lineage, including accepted-but-unconfirmed cancellations.
          setNotice({
            tone: 'error',
            text: `Cancellation incomplete — Needs attention. Schwab confirmed ${confirmation.confirmedCount || 0} of ${tracked.length} tracked order(s) canceled. ${remaining.length} remain live, filled, or unverified. The setup was NOT retired.`,
          })
          setDeleteConfirmOpen(false)
          if (onPositionRefresh) await onPositionRefresh()
          return false
        }

        await retireUnfilledSetup(supabase, project, { userId: user.id, brokerCancellationConfirmed: true })
        setupRetired = true
        setNotice(null)
        setDeleteConfirmOpen(false)
        if (onPositionRefresh) await onPositionRefresh()
        if (onMetadataChanged) onMetadataChanged()
        return true
      }

      if (cancelPayloads.length === 0) {
        // The pre-cancel active-order view can omit terminal or unavailable
        // orders. Require direct exact-ID confirmation before retirement.
        await confirmAndFinalize()
        return
      }

      const result = await executeCancelPayloads(cancelPayloads, session || {}, { brokerWriteProof })

      if (result.failureCount === 0) {
        // DELETE success is an acknowledgement, not broker-terminal truth.
        // Keep the card until every exact parent confirms CANCELED.
        await confirmAndFinalize()
      } else {
        // Preserve every reference until exact terminal confirmation; an accepted
        // cancellation may still be working and must remain available on retry.
        setNotice({ tone: 'error', text: `Cancellation incomplete — Needs attention. Schwab accepted ${result.successCount || 0} of ${result.attempted || tracked.length} cancellation requests. The setup and all tracked references are retained until cancellation is confirmed.` })
        setDeleteConfirmOpen(false)
        if (onPositionRefresh) await onPositionRefresh()
        // Intentionally NO onMetadataChanged — metadata was preserved.
      }
    } catch (err) {
      setNotice({ tone: 'error', text: err?.message || 'Could not retire setup. Refresh to reconcile its current state.' })
    } finally {
      setActionPending(false)
      if (!setupRetired) onCancellationPendingChange?.(project.id, false)
    }
  }, [user, project, readOnly, biometricGate, symbol, onPositionRefresh, onMetadataChanged, onCancellationPendingChange])

  // ── Derived flags ────────────────────────────────────────────────────────
  const trackedOrders = Array.isArray(project?.schwab_order_ids) ? project.schwab_order_ids : []
  const trackedCount = trackedOrders.length
  // Existing rows created before live-order state truth shipped can carry
  // setup_status='failed' even though their independently successful orders
  // are still tracked. Prefer the broker handle evidence immediately at
  // render time; the persistence path above prevents new rows from entering
  // this contradictory state.
  const savedSetupStatus = project?.setup_status === 'failed' && trackedCount > 0
    ? 'working'
    : project?.setup_status
  const effectiveSetupStatus = savedSetupStatus === 'closed' ? savedSetupStatus : (brokerEntryStatus || savedSetupStatus)
  const isWorking = effectiveSetupStatus === 'working'
  const expectedAccountNumbers = new Set(
    (Array.isArray(project?.account_sizing) ? project.account_sizing : [])
      .filter(row => Number(row?.shares) > 0)
      .map(row => String(row?.account_number ?? '').trim())
      .filter(Boolean)
  )
  const trackedAccountNumbers = new Set(
    trackedOrders
      .map(row => String(row?.accountNumber ?? row?.account_number ?? '').trim())
      .filter(Boolean)
  )
  const hasMissingSelectedAccount = [...expectedAccountNumbers]
    .some(accountNumber => !trackedAccountNumbers.has(accountNumber))
  const hasIncompleteSplitPair = [...expectedAccountNumbers].some(accountNumber => {
    const roles = new Set(trackedOrders
      .filter(row => String(row?.accountNumber ?? row?.account_number ?? '').trim() === accountNumber)
      .map(row => String(row?.role || '').toUpperCase()))
    return roles.has('TARGET_ENTRY') !== roles.has('RUNNER_ENTRY')
  })
  const hasCleanupOrphan = trackedOrders.some(row => row?.role === 'ORPHAN_NEEDS_CLEANUP')
  const needsAttention = isWorking
    && trackedCount > 0
    && (hasMissingSelectedAccount || hasIncompleteSplitPair || hasCleanupOrphan)
  const editDisabled = hasEntryFill || (isWorking && trackedCount === 0)
  const editDisabledReason = hasEntryFill
    ? 'Entry fills are confirmed. Manage the position and remaining orders from their dedicated controls.'
    : editDisabled
    ? 'No tracked Schwab order IDs (legacy / pre-EF-193 row). Cancel manually in ThinkorSwim first, then clear via Delete.'
    : null
  const setupTitle = String(project?.setup_pattern || '').trim() || 'Untitled setup'
  const cardCanOpenEdit = !readOnly && !stageForSending && !editDisabled
  const cardCanSelect = (readOnly || (stageForSending && !hasEntryFill)) && typeof onSelect === 'function'
  const isCancellationPending = cancellationPending || (deleteConfirmOpen && actionPending)

  const toggleAccount = (acctNum) => {
    setSelectedAccounts(prev => {
      const next = new Set(prev)
      if (next.has(acctNum)) next.delete(acctNum)
      else next.add(acctNum)
      return next
    })
  }

  const openEditPanel = useCallback(() => {
    if (!cardCanOpenEdit) return
    resetEditDraftFromProject()
    setDetailsOpen(false)
    setEditOpen(true)
  }, [cardCanOpenEdit, resetEditDraftFromProject])

  const handleCardClick = useCallback(() => {
    if (cardCanOpenEdit) {
      openEditPanel()
      return
    }
    if (cardCanSelect) onSelect(project?.id)
  }, [cardCanOpenEdit, cardCanSelect, onSelect, openEditPanel, project?.id])

  if (!project) return null

  return (
    <div className="w-full space-y-2">
      {!editOpen && <div className="rounded-lg border border-border bg-canvas p-2">
        <button type="button" onClick={() => { setEditOpen(false); setDetailsOpen(value => !value) }} className="font-mono text-[10px] text-inkMuted hover:text-ink">
          {detailsOpen ? 'Hide setup details' : 'Edit name and note'}
        </button>
        {detailsOpen && <SetupDetailsEditor key={project.id} project={project} onSaved={onPositionRefresh} />}
        {!detailsOpen && project.setup_notes && <div className="mt-1 whitespace-pre-wrap break-words font-mono text-[10px] text-inkDim">{project.setup_notes}</div>}
      </div>}
      {/* ── In-card notice ─────────────────────────────────────────────── */}
      <ActionSuccessToast message={notice?.tone === 'success' ? notice.text : null} onDismiss={() => setNotice(null)} testId="working-setup-notice" />
      {notice && notice.tone !== 'success' && (
        <div
          data-testid="working-setup-notice"
          data-tone={notice.tone}
          className={`flex items-start justify-between gap-2 px-3 py-2 rounded-lg border ${
            notice.tone === 'success'
              ? 'border-forest/30 bg-forest/10 text-forest'
              : 'border-ember/30 bg-ember/10 text-ember'
          }`}
        >
          <div className="font-mono text-[11px] leading-relaxed">{notice.text}</div>
          <button
            onClick={() => setNotice(null)}
            className="font-mono text-[9px] uppercase tracking-widest opacity-70 hover:opacity-100"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* ── Delete confirm dialog (inline, swaps the card) ────────────── */}
      {deleteConfirmOpen && !isCancellationPending ? (
        <div
          data-testid="delete-confirm-dialog"
          className="w-full rounded-lg border border-ember/30 bg-canvas p-3"
        >
          <div className="font-mono text-[11px] text-ink mb-3 leading-relaxed">
            {trackedCount > 0
              ? `Cancel this setup's ${trackedCount} tracked Schwab order(s), confirm cancellation, then retire the setup. Its prices, sizing, and order history will be retained.`
              : (isWorking || project.stage === 'working') && trackedCount === 0
                ? `This working setup has no tracked order IDs. Cancellation is blocked until its broker orders are reconciled.`
                : `Cancel this idea and move it to setup history. Its original prices and sizing will be retained. No broker orders will be sent.`}
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="delete-confirm-fire"
              onClick={handleDeleteFire}
              disabled={actionPending}
              className="font-mono text-[10px] font-semibold px-3 py-1.5 rounded-lg border border-ember bg-ember/10 text-ember hover:bg-ember/20 transition-colors disabled:opacity-40"
            >
              {actionPending ? 'Working…' : 'Cancel Setup'}
            </button>
            <button
              data-testid="delete-confirm-dismiss"
              onClick={() => setDeleteConfirmOpen(false)}
              disabled={actionPending}
              className="font-mono text-[10px] px-3 py-1.5 rounded-lg border border-border text-inkMuted hover:text-ink transition-colors disabled:opacity-40"
            >
              Keep
            </button>
          </div>
        </div>
      ) : repairOpen && repairPlan ? (
        <div
          data-testid="setup-repair-panel"
          className="w-full rounded-lg border border-ember/30 bg-canvas p-3 space-y-3"
        >
          <div>
            <div className="font-mono text-[11px] font-semibold text-ink">Repair missing orders</div>
            <div className="mt-1 font-mono text-[10px] leading-relaxed text-inkMuted">
              Schwab was refreshed. Only the incomplete account{repairPlan.orderSummaries.length === 1 ? '' : 's'} below will be changed.
            </div>
          </div>
          <div className="space-y-1.5">
            {repairPlan.orderSummaries.map(row => (
              <div key={row.accountNumber} className="rounded border border-border bg-canvas2/40 px-2 py-2">
                <div className="font-mono text-[10px] font-semibold text-ink">{row.accountName}</div>
                <div className="font-mono text-[10px] text-inkMuted">
                  {row.quantity} shares{row.splitBracket
                    ? ` · ${row.sellQty} target · ${row.runnerQty} runner`
                    : ' · single entry'}
                </div>
              </div>
            ))}
          </div>
          {repairPlan.cancelPayloads.length > 0 && (
            <div className="font-mono text-[10px] text-ember">
              An incomplete live leg will be canceled first. If that cancel fails, replacement orders will not be sent.
            </div>
          )}
          <div className="flex items-center gap-2">
            <button
              onClick={confirmRepair}
              className="font-mono text-[10px] font-semibold px-3 py-1.5 rounded-lg border border-ember/50 bg-ember/10 text-ember hover:bg-ember/20"
            >
              Review and Confirm
            </button>
            <button
              onClick={() => { setRepairOpen(false); setRepairPlan(null) }}
              className="font-mono text-[10px] px-3 py-1.5 rounded-lg border border-border text-inkMuted hover:text-ink"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : editOpen ? (
        /* ── Edit panel (inline) ─────────────────────────────────────── */
        <div
          data-testid="working-setup-edit-panel"
          className="w-full rounded-lg border border-[#c4953a]/40 bg-canvas p-3 space-y-2"
        >
          {terminalIdsBanner && (
            <div className="font-mono text-[10px] text-inkMuted bg-canvas2/40 border border-border rounded px-2 py-1">
              All previously placed orders are already terminal at Schwab. Placing fresh orders only.
            </div>
          )}
          <SetupDetailsEditor key={project.id} project={project} onSaved={onPositionRefresh} />
          <div className="border-t border-border pt-2 font-mono text-[10px] text-inkMuted">Order changes</div>
          <div className="flex items-center gap-2">
            <label className="font-mono text-[10px] uppercase tracking-widest text-inkMuted shrink-0 w-16">Entry</label>
            <button
              onClick={() => setEntryType(entryType === 'On Strength' ? 'Pullback' : 'On Strength')}
              className={`font-mono text-[10px] px-2 py-1 rounded border ${
                entryType === 'On Strength'
                  ? 'border-forest/40 bg-forest/10 text-forest'
                  : 'border-violet-400/40 bg-violet-500/10 text-violet-400'
              }`}
            >
              {entryType}
            </button>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <label className="block">
              <div className="font-mono text-[9px] uppercase tracking-widest text-inkMuted">Trigger</div>
              <input
                type="number" step="0.01" value={triggerPrice}
                onChange={e => setTriggerPrice(e.target.value)}
                className="w-full bg-canvas2 border border-border rounded px-2 py-1 font-mono text-[11px] text-ink"
              />
            </label>
            <label className="block">
              <div className="font-mono text-[9px] uppercase tracking-widest text-inkMuted">Stop</div>
              <SetupStopInput entryPrice={triggerPrice} value={stopPrice} onChange={setStopPrice} className="w-full bg-canvas2 border border-border rounded px-2 py-1 font-mono text-[11px] text-ink" />
            </label>
            <label className="block">
              <div className="font-mono text-[9px] uppercase tracking-widest text-inkMuted">Target</div>
              <input
                type="number" step="0.01" value={targetPrice}
                onChange={e => setTargetPrice(e.target.value)}
                className="w-full bg-canvas2 border border-border rounded px-2 py-1 font-mono text-[11px] text-ink"
              />
            </label>
          </div>
          <div className="flex items-center gap-2">
            <label className="font-mono text-[10px] uppercase tracking-widest text-inkMuted shrink-0 w-16">Size %</label>
            <input
              type="number" step="0.1" value={sizePct}
              onChange={e => setSizePct(e.target.value)}
              className="w-20 bg-canvas2 border border-border rounded px-2 py-1 font-mono text-[11px] text-ink"
            />
          </div>
          {accounts.length > 0 && (
            <div className="space-y-1">
              <div className="font-mono text-[9px] uppercase tracking-widest text-inkMuted">Accounts</div>
              {accountSizing.map(a => {
                const isSel = selectedAccounts.has(a.account_number)
                return (
                  <div
                    key={a.account_number}
                    className="flex items-center gap-2 cursor-pointer"
                    onClick={() => toggleAccount(a.account_number)}
                  >
                    <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center ${
                      isSel ? 'bg-[#c4953a] border-[#c4953a]' : 'border-border bg-canvas'
                    }`}>
                      {isSel && <Check size={10} className="text-white" strokeWidth={3} />}
                    </div>
                    <span className="font-mono text-[10px] text-ink">
                      {getNickname(a.account_number) || `****${String(a.account_number).slice(-4)}`}
                    </span>
                    <span className="font-mono text-[10px] text-inkMuted">
                      {a.shares} sh · risk <PrivateValue>{fmtCurrency(a.dollarRisk)}</PrivateValue>
                    </span>
                  </div>
                )
              })}
            </div>
          )}
          {editError && (
            <div className="font-mono text-[10px] text-ember">{editError}</div>
          )}
          <div className="flex items-center gap-2">
            <button
              data-testid="save-working-setup-edit-btn"
              onClick={handleEditSave}
              disabled={actionPending}
              className="font-mono text-[10px] font-semibold px-3 py-1.5 rounded-lg border border-[#c4953a] bg-[#c4953a]/10 text-[#c4953a] hover:bg-[#c4953a]/20 transition-colors disabled:opacity-40"
            >
              {actionPending ? 'Working…' : 'Review order changes'}
            </button>
            <button
              data-testid="cancel-working-setup-edit-btn"
              onClick={() => { resetEditDraftFromProject(); setEditOpen(false) }}
              disabled={actionPending}
              className="font-mono text-[10px] px-3 py-1.5 rounded-lg border border-border text-inkMuted hover:text-ink transition-colors disabled:opacity-40"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        /* ── Setup card with always-visible Edit + Delete ──────────────── */
        <div
          data-testid="working-setup-card"
          aria-busy={isCancellationPending ? 'true' : 'false'}
          onClick={!isCancellationPending && (cardCanOpenEdit || cardCanSelect) ? handleCardClick : undefined}
          className={`w-full rounded-lg p-3 relative overflow-hidden border-2 border-white/80 bg-blue-500/[0.06] shadow-[0_0_20px_rgba(59,130,246,0.5)] ${!isCancellationPending && (cardCanOpenEdit || cardCanSelect) ? 'cursor-pointer' : ''}`}
        >
          <div
            data-testid="working-setup-card-content"
            aria-hidden={isCancellationPending ? 'true' : undefined}
            className={`transition-[filter,opacity] duration-200 ${isCancellationPending ? 'pointer-events-none select-none blur-[2px] opacity-30' : ''}`}
          >
          <div className="mb-1.5 pr-12 font-mono text-[11px] font-semibold text-ink break-words">{setupTitle}</div>
          <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
            {project.entry_type && (
              <span className={`font-mono text-[8px] px-1.5 py-0.5 rounded-full font-semibold border whitespace-nowrap ${
                project.entry_type === 'On Strength'
                  ? 'bg-forest/10 text-forest border-forest/30'
                  : 'bg-violet-500/10 text-violet-400 border-violet-400/30'
              }`}>
                {project.entry_type}
              </span>
            )}
            <span data-testid="setup-status-badge" className={`font-mono text-[8px] px-1.5 py-0.5 rounded-full border uppercase tracking-wider whitespace-nowrap ${
              effectiveSetupStatus === 'working' ? 'bg-[#c4953a]/10 text-[#c4953a] border-[#c4953a]/30'
              : hasEntryFill ? 'bg-forest/10 text-forest border-forest/30'
              : effectiveSetupStatus === 'failed' ? 'bg-ember/10 text-ember border-ember/30'
              : effectiveSetupStatus === 'closed' ? 'bg-canvas2 text-inkMuted border-border'
              : 'bg-blue-500/10 text-blue-400 border-blue-400/30'
            }`}>
              {effectiveSetupStatus || 'idea'}
            </span>
            {needsAttention && (
              <span
                data-testid="setup-attention-badge"
                className="font-mono text-[8px] px-1.5 py-0.5 rounded-full border uppercase tracking-wider whitespace-nowrap bg-ember/10 text-ember border-ember/30"
                title="One or more selected accounts has incomplete live-order coverage. Review order details before changing or retrying this setup."
              >
                Needs attention
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 font-mono text-[10px] tabular-nums mb-1">
            <span className="text-forest"><PrivateValue>{fmtCurrency(project.trigger_price)}</PrivateValue></span>
            <span className="text-ember"><PrivateValue>{fmtCurrency(project.pattern_failure_price)}</PrivateValue></span>
            <span className="text-blue-400"><PrivateValue>{fmtCurrency(project.sell_target_price)}</PrivateValue></span>
          </div>
          {project.setup_source && (
            <div className="font-mono text-[9px] text-inkDim truncate">
              {project.setup_source}
              {project.recommended_size_pct != null && ` · Size ${Number(project.recommended_size_pct).toFixed(1)}%`}
            </div>
          )}
          {needsAttention && !readOnly && !isCancellationPending && (
            <button
              onClick={(event) => { event.stopPropagation(); prepareRepair() }}
              disabled={actionPending}
              className="mt-2 rounded-lg border border-ember/40 bg-ember/10 px-2.5 py-1.5 font-mono text-[9px] font-semibold uppercase tracking-wider text-ember hover:bg-ember/20 disabled:opacity-40"
            >
              {actionPending ? 'Refreshing Schwab…' : 'Repair missing orders'}
            </button>
          )}
          {!readOnly && !isCancellationPending && (
          <div className="absolute top-2 right-2 flex items-center gap-1.5 z-10">
            <button
              data-testid="edit-working-setup-btn"
              onClick={(event) => {
                event.stopPropagation()
                if (stageForSending && cardCanSelect) onSelect(project?.id)
                else openEditPanel()
              }}
              disabled={hasEntryFill || (!stageForSending && editDisabled)}
              title={stageForSending ? 'Edit and send setup' : (editDisabledReason || 'Edit setup')}
              className="p-1 rounded text-inkDim hover:text-ink hover:bg-canvas2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Pencil size={11} />
            </button>
            <button
              data-testid="delete-working-setup-btn"
              onClick={(event) => { event.stopPropagation(); setDeleteConfirmOpen(true) }}
              title="Cancel setup"
              className="p-1 rounded text-inkDim hover:text-ember hover:bg-ember/10 transition-colors"
            >
              <Trash2 size={11} />
            </button>
          </div>
          )}
          </div>
          {isCancellationPending && (
            <div
              data-testid="working-setup-canceling"
              role="status"
              aria-live="polite"
              className="absolute inset-0 z-20 flex items-center justify-center bg-canvas/45 backdrop-brightness-75"
            >
              <span className="flex items-center gap-1.5 rounded-full border border-[#c4953a]/35 bg-canvas/90 px-3 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-widest text-[#c4953a] shadow-sm">
                Cancelling
                <span aria-hidden="true" className="inline-flex items-end gap-0.5">
                  <span className="animate-bounce [animation-delay:-0.30s]">.</span>
                  <span className="animate-bounce [animation-delay:-0.15s]">.</span>
                  <span className="animate-bounce">.</span>
                </span>
              </span>
            </div>
          )}
        </div>
      )}

      {/* ── Modal — Edit confirmation flow ─────────────────────────────── */}
      <OrderConfirmationModal
        open={gateway.confirmState.open}
        action={gateway.confirmState.action}
        symbol={gateway.confirmState.symbol}
        orders={gateway.confirmState.orders}
        loading={gateway.confirmState.loading}
        results={gateway.confirmState.results}
        onConfirm={gateway.confirmOrder}
        onClose={gateway.cancelConfirmation}
      />

      <OrderConfirmationModal
        open={repairGateway.confirmState.open}
        action={repairGateway.confirmState.action}
        symbol={repairGateway.confirmState.symbol}
        orders={repairGateway.confirmState.orders}
        loading={repairGateway.confirmState.loading}
        results={repairGateway.confirmState.results}
        onConfirm={repairGateway.confirmOrder}
        onClose={repairGateway.cancelConfirmation}
      />

      {/* ── EF-193 P2: modal footer banner for cancel-skipped PLACE rows ── */}
      {gateway.confirmState.results && gateway.confirmState.results.some(r => r.skippedDueToCancelFailure) && (
        <div
          data-testid="working-setup-skip-banner"
          className="font-mono text-[10px] text-ember bg-ember/10 border border-ember/30 rounded px-3 py-2"
        >
          Replacement orders were not placed because one or more cancel orders failed.
          Live broker state may be inconsistent — review Working Orders before retrying.
        </div>
      )}
    </div>
  )
}
